import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import ReactFlow, {
  ReactFlowProvider,
  Background,
  Controls,
  ControlButton,
  MiniMap,
  Panel,
  PanOnScrollMode,
  MarkerType,
  addEdge,
  useEdgesState,
  useNodesState,
  useReactFlow,
  type Connection,
  type Edge,
  type Node,
} from 'reactflow';
import 'reactflow/dist/style.css';
import {
  Box,
  TextField,
  Button,
  Paper,
  Tooltip,
  IconButton,
  InputAdornment,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { useAuth } from '../../../contexts/AuthContext';
import { useTheme as usePrism7Theme } from '../../../contexts/ThemeContext';
import PrimitiveNode, { LayoutDirectionContext, type LayoutDirection, type PrimitiveNodeData } from './PrimitiveNode';
import WorkflowEdge, { type WorkflowEdgeData } from './WorkflowEdge';
import WorkflowPalette from './WorkflowPalette';
import WorkflowInspectorPanel from './WorkflowInspectorPanel';
import WorkflowDraftPanel from './WorkflowDraftPanel';
import WorkflowRunTestPanel from './WorkflowRunTestPanel';
import WorkflowToolbar from './WorkflowToolbar';
import EmptyWorkflowState from './EmptyWorkflowState';
import { docToRfNodes, docToRfEdges, rfToDoc, PRIMITIVE_NODE_RF_TYPE } from './workflowGraphAdapters';
import {
  defaultConfigFor,
  DRAG_MIME_PRIMITIVE,
  DRAG_MIME_ACTION,
  type PrimitiveType,
  type WorkflowVersionSummary,
  type CatalogAction,
  type CatalogActionsByCategory,
  type CatalogConnection,
} from './workflowGraphTypes';
import {
  getWorkflowConfig,
  getWorkflowConfigVersion,
  getWorkflowVersions,
  getWorkflowNames,
  validateWorkflow,
  saveWorkflowConfig,
  draftWorkflow,
  startRunTest,
  isReservedWorkflowName,
  type WorkflowBackend,
  type DraftWorkflowResponse,
  type WorkflowNameSummary,
  type RunTestRequest,
} from '../../../services/workflowConfigService';
import { getCatalogActions, getCatalogConnections } from '../../../services/catalogService';
import { appsService } from '../../../services/appsService';
import ChannelRuleFlowsView, { type ChannelRuleFlowsViewHandle } from './ChannelRuleFlowsView';
import { formatEdgeConditionLabel, type BranchCondition } from './edgeConditionUtils';

const FieldHelp: React.FC<{ text: string }> = ({ text }) => (
  <Tooltip
    title={<Box component="span" sx={{ whiteSpace: 'pre-line', display: 'block', fontSize: '0.75rem' }}>{text}</Box>}
    placement="top"
    arrow
    slotProps={{ tooltip: { sx: { maxWidth: 360 } } }}
  >
    <IconButton size="small" sx={{ p: 0.25, color: 'text.secondary' }} tabIndex={-1}>
      <InfoOutlinedIcon sx={{ fontSize: 15 }} />
    </IconButton>
  </Tooltip>
);

const nodeTypes = { [PRIMITIVE_NODE_RF_TYPE]: PrimitiveNode };
const WORKFLOW_EDGE_RF_TYPE = 'workflowEdge';

const edgeTypes = { [WORKFLOW_EDGE_RF_TYPE]: WorkflowEdge };
// NEW — arrowhead pointing at the target end of every edge, matching the reference flow. A
// plain module-level constant (not recreated per render) passed via defaultEdgeOptions, which
// ReactFlow applies to every edge without needing each edge object to carry its own markerEnd.
const defaultEdgeOptions = { markerEnd: { type: MarkerType.ArrowClosed, color: '#9e9e9e', width: 18, height: 18 } };

// Same wording used in RuleFlowCanvas.tsx's own lock button — kept as separate copies (not a
// shared import) since these two canvases are deliberately independent everywhere else; update
// both if this text ever changes.
const INTERACTIVE_TOGGLE_TOOLTIP =
  'Interactive (On): Fully editable — add, drag, and configure nodes or connections.\n' +
  'Non-Interactive (Off): Canvas locked — freezes elements to prevent accidental edits while viewing, presenting, or testing.';

// NEW — shared between the toolbar's Load button and the Versions dropdown (both ultimately call
// loadConfigByName), so an accepted-but-unsaved business-rule draft can't be silently overwritten
// through either path. See PR review M1/M2 — this used to only guard the Load button.
const LOADED_VIA_DRAFT_REASON =
  'Disabled — this canvas was populated from an accepted business rule draft that hasn\'t been saved yet. Click "Unlock" to load anyway (discarding the draft), or Save first to lift this automatically.';

// One counter per primitive type — so a node's default name/number reflects how many of
// *that type* have been added (e.g. the 2nd Call API node is "CALL_API 2"), not how many
// nodes of any type were ever created. Node ids stay unique across the graph regardless,
// since each id is prefixed by its type (call_api_2 vs notify_2 can't collide).
const nodeIdCountersByType: Partial<Record<PrimitiveType, number>> = {};
function nextNodeId(type: PrimitiveType): { id: string; ordinal: number } {
  const ordinal = (nodeIdCountersByType[type] ?? 0) + 1;
  nodeIdCountersByType[type] = ordinal;
  return { id: `${type.toLowerCase()}_${ordinal}`, ordinal };
}

/** Same grid formula the palette's click-to-add has always used — unchanged. */
function gridPositionFor(existingCount: number) {
  return { x: 120 + (existingCount % 4) * 220, y: 120 + Math.floor(existingCount / 4) * 160 };
}

function buildPrimitiveNode(type: PrimitiveType, position: { x: number; y: number }): Node<PrimitiveNodeData> {
  const { id, ordinal } = nextNodeId(type);
  return {
    id,
    type: PRIMITIVE_NODE_RF_TYPE,
    position,
    data: { primType: type, name: `${type} ${ordinal}`, config: defaultConfigFor(type) },
  };
}

function buildActionNode(action: CatalogAction, position: { x: number; y: number }): Node<PrimitiveNodeData> {
  const { id } = nextNodeId(action.compiles_to);
  return {
    id,
    type: PRIMITIVE_NODE_RF_TYPE,
    position,
    data: {
      primType: action.compiles_to,
      actionId: action.action_id,
      actionLabel: action.label,
      name: action.label,
      config: {},
      inputs: {},
      // NEW — display-only; resolved to a real app icon via displayNodes below. Never sent to
      // the backend (rfToDoc whitelists its own fields and never reads connectionId).
      connectionId: action.connection_id,
    },
  };
}

/**
 * NEW — "Auto-layout" (opt-in, button-triggered only — never runs automatically, so Load
 * always shows exactly the positions that were saved, unchanged). Arranges nodes by graph depth
 * (BFS/longest-path from each source node); siblings at the same depth spread out along the
 * cross-axis.
 *
 * `direction` alternates per click (handled by the caller): 'vertical' stacks depth top-to-bottom
 * with siblings spread horizontally (rows); 'horizontal' lays depth out left-to-right with
 * siblings spread vertically (columns) — matching the reference n8n-style flow.
 *
 * Handle positions in PrimitiveNode.tsx (via LayoutDirectionContext) always match whichever
 * direction was just applied here — top/bottom for vertical, left/right for horizontal — so
 * edges never have to loop around a node body to reach a handle on the "wrong" side. That
 * mismatch (not a true horizontal layout itself) was the actual cause of an earlier bug where
 * edge condition labels ("approved"/"rejected"/"timeout") got hidden.
 * No new dependency (e.g. dagre) — the graphs here are small enough that a plain layered
 * layout is enough, and it keeps this change to pure in-repo logic.
 */
/**
 * NEW — infers which direction a just-loaded graph (Load, or an accepted AI draft) actually
 * flows in, from its nodes' real position spread, instead of leaving `layoutDirection` at
 * whatever it happened to be before (it always starts at the 'vertical' default on a fresh
 * mount). Without this, a saved graph with wide horizontal positions would render with
 * top/bottom connector handles anyway — mismatched with its own layout, producing exactly the
 * zigzag/looping connections this whole direction-matching setup exists to avoid.
 */
function inferLayoutDirection(nodes: Node<PrimitiveNodeData>[]): LayoutDirection {
  if (nodes.length < 2) return 'vertical';
  const xs = nodes.map((n) => n.position.x);
  const ys = nodes.map((n) => n.position.y);
  const xSpread = Math.max(...xs) - Math.min(...xs);
  const ySpread = Math.max(...ys) - Math.min(...ys);
  return xSpread > ySpread ? 'horizontal' : 'vertical';
}

function computeAutoLayout(
  nodes: Node<PrimitiveNodeData>[],
  edges: Edge[],
  direction: LayoutDirection = 'vertical'
): Record<string, { x: number; y: number }> {
  const LAYER_GAP = 240; // gap between depths along the primary axis
  const SIBLING_GAP = 160; // gap between siblings at the same depth, along the cross-axis
  const layerByNodeId: Record<string, number> = {};
  nodes.forEach((n) => {
    layerByNodeId[n.id] = 0;
  });
  // Longest-path relaxation, bounded by node count so a cycle can't loop forever.
  for (let pass = 0; pass < nodes.length; pass += 1) {
    let changed = false;
    edges.forEach((e) => {
      if (layerByNodeId[e.source] === undefined || layerByNodeId[e.target] === undefined) return;
      if (layerByNodeId[e.target] < layerByNodeId[e.source] + 1) {
        layerByNodeId[e.target] = layerByNodeId[e.source] + 1;
        changed = true;
      }
    });
    if (!changed) break;
  }

  const nodeIdsByLayer: Record<number, string[]> = {};
  nodes.forEach((n) => {
    const layer = layerByNodeId[n.id] ?? 0;
    (nodeIdsByLayer[layer] ??= []).push(n.id);
  });

  const widestLayerCount = Math.max(1, ...Object.values(nodeIdsByLayer).map((ids) => ids.length));
  const crossAxisSpan = (widestLayerCount - 1) * SIBLING_GAP;

  const positions: Record<string, { x: number; y: number }> = {};
  Object.keys(nodeIdsByLayer)
    .map(Number)
    .sort((a, b) => a - b)
    .forEach((layer) => {
      const idsInLayer = nodeIdsByLayer[layer];
      const layerSpan = (idsInLayer.length - 1) * SIBLING_GAP;
      const crossAxisStart = 120 + (crossAxisSpan - layerSpan) / 2; // center narrower layers under wider ones
      idsInLayer.forEach((id, i) => {
        positions[id] =
          direction === 'horizontal'
            ? { x: 120 + layer * LAYER_GAP, y: crossAxisStart + i * SIBLING_GAP }
            : { x: crossAxisStart + i * SIBLING_GAP, y: 120 + layer * LAYER_GAP };
      });
    });
  return positions;
}

/**
 * NEW — condition labels ("approved", "timeout"...) render with a background chip so they
 * stay legible when an edge crosses near or behind a node, instead of blending into whatever
 * is underneath. Purely visual React Flow edge props — rfToDoc never reads them, so this has
 * zero effect on the saved/validated graph payload.
 */
const EDGE_LABEL_STYLE = { fontSize: 11, fontWeight: 700, fill: '#37474f' };
const EDGE_LABEL_BG_STYLE = { fill: '#ffffff', fillOpacity: 0.92 };
const EDGE_LABEL_BG_PADDING: [number, number] = [6, 3];

interface CanvasSurfaceProps {
  displayNodes: Node<PrimitiveNodeData>[];
  edges: Edge[];
  onNodesChange: ReturnType<typeof useNodesState<PrimitiveNodeData>>[2];
  onEdgesChange: ReturnType<typeof useEdgesState>[2];
  onConnect: (connection: Connection) => void;
  onNodeClick: (e: React.MouseEvent, node: Node) => void;
  onEdgeClick: (e: React.MouseEvent, edge: Edge) => void;
  onPaneClick: () => void;
  onAddNodeAt: (node: Node<PrimitiveNodeData>) => void;
  onAutoLayout: () => void;
  /** NEW — re-opens the "Describe a business rule" gate screen from the canvas toolbar. */
  onOpenDraftPanel: () => void;
  /** NEW — opens the n8n-style "+" node picker drawer. */
  onOpenPicker: () => void;
  /** NEW — disables the "+" add-step button, e.g. until a workflow name has been entered.
   * Defaults to false so this never changes existing behavior for any other caller. */
  addStepDisabled?: boolean;
  /** Starts a real run-test of whatever's currently on the canvas, no explicit dry_run override
   * (the backend defaults an inline-graph run-test to dry_run=true). Separate from Save — this
   * never persists a version, it only starts a Temporal-backed test run. */
  onDryRunSave: () => void;
  /** Starts a real run-test with dry_run explicitly false — the only path that causes live
   * side effects (real notifications, live external API calls) when the run executes. */
  onExecute: () => void;
  /** NEW — shared disabled condition for both buttons above (same as the toolbar's Save button),
   * OR'd with runTestBusy so a second click can't fire while the first run-test call is in flight. */
  saveActionsDisabled: boolean;
  /** NEW — true while a run-test start request is in flight (either button). */
  runTestBusy: boolean;
  /** NEW — set if the last run-test start attempt failed; shown inline next to the buttons since
   * the run panel itself never opens in that case. */
  runTestError: string | null;
}

/**
 * Rendered inside <ReactFlowProvider> so it can call useReactFlow() for screenToFlowPosition —
 * needed to convert a drop's screen coordinates into canvas coordinates (accounts for pan/zoom).
 * Click-to-add from the palette is untouched; this only adds a second, additive way to place a node.
 */
const CanvasSurface: React.FC<CanvasSurfaceProps> = ({
  displayNodes,
  edges,
  onNodesChange,
  onEdgesChange,
  onConnect,
  onNodeClick,
  onEdgeClick,
  onPaneClick,
  onAddNodeAt,
  onAutoLayout,
  onOpenDraftPanel,
  onOpenPicker,
  addStepDisabled = false,
  onDryRunSave,
  onExecute,
  saveActionsDisabled,
  runTestBusy,
  runTestError,
}) => {
  const { screenToFlowPosition, fitView } = useReactFlow();
  // Drives the lock/interactive toggle ourselves (instead of the default Controls button) only
  // so we can give it custom tooltip text — functionally identical to what the default
  // "toggle interactivity" button already did.
  const [interactive, setInteractive] = useState(true);

  // NEW — once the canvas goes from empty to populated in one batch (Load, or an accepted
  // business-rule draft), re-center and fit the whole graph in view. Only fires on that
  // empty→populated transition, so it never fights manual one-by-one node placement or
  // interrupts an in-progress pan/zoom while already working on a loaded graph.
  const prevNodeCountRef = useRef(0);
  useEffect(() => {
    if (prevNodeCountRef.current === 0 && displayNodes.length > 0) {
      window.setTimeout(() => fitView({ padding: 0.2, duration: 300 }), 50);
    }
    prevNodeCountRef.current = displayNodes.length;
  }, [displayNodes.length, fitView]);

  const handleAutoLayoutClick = () => {
    onAutoLayout();
    // Positions update async via React state — wait a tick so ReactFlow has the new
    // coordinates before fitView measures the graph's bounding box.
    window.setTimeout(() => fitView({ padding: 0.2, duration: 300 }), 50);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const actionJson = e.dataTransfer.getData(DRAG_MIME_ACTION);
    const primitiveType = e.dataTransfer.getData(DRAG_MIME_PRIMITIVE);
    if (!actionJson && !primitiveType) return;

    const position = screenToFlowPosition({ x: e.clientX, y: e.clientY });
    if (actionJson) {
      const action = JSON.parse(actionJson) as CatalogAction;
      onAddNodeAt(buildActionNode(action, position));
    } else {
      onAddNodeAt(buildPrimitiveNode(primitiveType as PrimitiveType, position));
    }
  };

  return (
    <div
      style={{ width: '100%', height: '100%' }}
      onDragOver={(e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
      }}
      onDrop={handleDrop}
    >
      <ReactFlow
        nodes={displayNodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        defaultEdgeOptions={defaultEdgeOptions}
        onNodeClick={onNodeClick}
        onEdgeClick={onEdgeClick}
        onPaneClick={onPaneClick}
        nodesDraggable={interactive}
        nodesConnectable={interactive}
        elementsSelectable={interactive}
        fitView
        panOnScroll
        panOnScrollMode={PanOnScrollMode.Free}
        zoomOnScroll={false}
      >
        <Background />
        {/* Default zoom in/out and fit view buttons already carry native title attributes out of
            the box. Only the lock/interactive button is replaced (showInteractive={false} hides
            the default one) so it can carry the custom explanation text below instead of the
            generic "toggle interactivity". */}
        <Controls showInteractive={false}>
          <ControlButton title={INTERACTIVE_TOGGLE_TOOLTIP} onClick={() => setInteractive((v) => !v)}>
            {interactive ? <LockOpenIcon sx={{ fontSize: 16 }} /> : <LockIcon sx={{ fontSize: 16 }} />}
          </ControlButton>
        </Controls>
        <MiniMap />
        <Panel position="top-left">
          <Tooltip title="Reopen the business rule description panel" placement="bottom" arrow>
            <Button
              size="small"
              variant="contained"
              onClick={onOpenDraftPanel}
              sx={{ textTransform: 'none' }}
            >
              Describe a business rule
            </Button>
          </Tooltip>
        </Panel>
        <Panel position="top-right">
          <Tooltip
            title="Automatically arranges nodes top-to-bottom in flow order and re-centers the view. Only repositions nodes — it never changes names, config, or connections, and never runs on its own."
            placement="bottom"
            arrow
          >
            <span>
              <Button
                size="small"
                variant="contained"
                onClick={handleAutoLayoutClick}
                disabled={displayNodes.length === 0}
                sx={{ textTransform: 'none', mr: 1 }}
              >
                Auto-layout
              </Button>
            </span>
          </Tooltip>
          <Tooltip title={addStepDisabled ? 'Enter a workflow name first' : 'Add a step'} placement="bottom" arrow>
            <span>
              <IconButton
                size="small"
                onClick={onOpenPicker}
                disabled={addStepDisabled}
                sx={{
                  backgroundColor: 'primary.main',
                  color: '#fff',
                  '&:hover': { backgroundColor: 'primary.dark' },
                }}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Panel>
        <Panel position="bottom-center" style={{ marginBottom: 12 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                p: 1,
                borderRadius: 2,
                backgroundColor: 'background.paper',
                boxShadow: 3,
              }}
            >
              <Tooltip title="Starts a real test run of the current canvas graph with dry_run=true — no real notifications sent, no live external API calls made" placement="top" arrow>
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={onDryRunSave}
                    disabled={saveActionsDisabled || runTestBusy}
                    sx={{ textTransform: 'none' }}
                  >
                    {runTestBusy ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
                    Dry run
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title="Starts a real test run of the current canvas graph with dry_run=false — real notifications and live external API calls will be made" placement="top" arrow>
                <span>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={onExecute}
                    disabled={saveActionsDisabled || runTestBusy}
                    sx={{ textTransform: 'none' }}
                  >
                    {runTestBusy ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
                    Execute
                  </Button>
                </span>
              </Tooltip>
            </Box>
            {runTestError && (
              <Alert severity="error" sx={{ maxWidth: 420 }}>
                {runTestError}
              </Alert>
            )}
          </Box>
        </Panel>
      </ReactFlow>
    </div>
  );
};

/**
 * Workflow Canvas — business rule graph editor (React Flow). Canvas left, controls right.
 * Ref: 04_Frontend_Canvas_Rule_To_Workflow_Changes.md
 */
/**
 * The original, company-wide "named workflow" builder — unchanged below this line. Business_rule_canvas
 * branch (PRISM7_CHANNEL_RULES_CANVAS_TDD.md §7) renames this internal component and wraps it
 * (see WorkflowCanvasTab further down) rather than editing any of its ~1000 lines of existing
 * empty/draft/canvas state machine, so every pre-existing behavior here is byte-for-byte identical
 * to before this branch.
 */
export interface LegacyWorkflowCanvasTabProps {
  /** dynamic_multiselect_FE_datasource_picker_spec — threaded down to WorkflowInspectorPanel so a
   * dynamic_multiselect catalog field (e.g. the datasource picker) can scope its live fetch to the
   * channel whose Settings drawer this "Channel Workflow" tab is rendered inside. Optional: when
   * this tab is reached with no channel context (e.g. a future non-channel-scoped embed), the
   * field renders its own "no channel context" state rather than guessing one. */
  channelId?: string;
}

const LegacyWorkflowCanvasTab: React.FC<LegacyWorkflowCanvasTabProps> = ({ channelId }) => {
  const { user } = useAuth();
  const createdBy = user?.email ?? user?.name ?? 'unknown';
  const { isDarkMode } = usePrism7Theme();

  const [nodes, setNodes, onNodesChange] = useNodesState<PrimitiveNodeData>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [selectedNode, setSelectedNode] = useState<Node<PrimitiveNodeData> | null>(null);
  const [selectedEdge, setSelectedEdge] = useState<Edge | null>(null);
  const [runningNodeId, setRunningNodeId] = useState<string | null>(null);

  const [configName, setConfigName] = useState('');
  /** PR #673 review (M1) — snapshot (rfToDoc, stringified) of the last loaded/saved document,
   * used by handleBackToChannelWorkflow to detect unsaved edits before discarding them. null
   * means nothing has been loaded/saved yet this session — any nodes on the canvas are
   * never-saved, manual or draft-built content. */
  const lastPersistedDocRef = useRef<string | null>(null);
  /** PR #673 review (M2) — bumped by handleBackToChannelWorkflow so a load/draft/save response
   * already in flight when the canvas is reset can detect it's stale (started before this
   * generation) and no-op instead of repopulating the canvas that was just cleared. */
  const resetGenerationRef = useRef(0);
  // NEW — company-wide "which workflow do you want to open" picker (GET /workflow-config/names).
  const [workflowNames, setWorkflowNames] = useState<WorkflowNameSummary[] | null>(null);
  const [workflowNamesLoading, setWorkflowNamesLoading] = useState(false);
  const [workflowNamesError, setWorkflowNamesError] = useState<string | null>(null);
  // NEW — "leave" is reserved by the backend for the one seed/demo config literally named
  // "leave" (backend now rejects backend="leave" for any other config with a 422). The
  // "Workflows" picker that used to let users choose generic/leave has been removed entirely —
  // every workflow built through this tab always sends "generic".
  const backend: WorkflowBackend = 'generic';
  // NEW — the Dry run checkbox was removed; dry-run vs. live save is now chosen per-click via
  // the "Dry run" / "Execute" buttons centered at the bottom of the canvas (see handleSave).

  const [loadError, setLoadError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[] | null>(null);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // NEW — the "Valid" and "Saved as version X" success banners only ever cleared on the next
  // Validate/Save call, so if the user switches to another Settings tab and back, they'd still
  // see a stale success message from whenever they last acted. Auto-dismiss just these two
  // success cases after a few seconds, like a toast — error banners (validation failures, save
  // errors) are left alone since those need the user's attention, not a timed disappearance.
  useEffect(() => {
    if (!saveMessage) return;
    const timer = setTimeout(() => setSaveMessage(null), 5000);
    return () => clearTimeout(timer);
  }, [saveMessage]);

  useEffect(() => {
    if (!validationErrors || validationErrors.length > 0) return;
    const timer = setTimeout(() => setValidationErrors(null), 5000);
    return () => clearTimeout(timer);
  }, [validationErrors]);

  const [draftLoading, setDraftLoading] = useState(false);
  const [draftError, setDraftError] = useState<string | null>(null);
  const [pendingDraft, setPendingDraft] = useState<DraftWorkflowResponse | null>(null);
  /**
   * NEW — n8n-style entry state machine for the tab:
   *  'empty'  → two-card chooser (EmptyWorkflowState): "Add first step" (manual) / "Build with AI".
   *  'draft'  → the "Describe a business rule" gate (business-rules flow).
   *  'canvas' → the populated/working canvas + sidebar.
   * Defaults to 'empty' so every new/empty workflow opens on the two-card entry screen first.
   */
  const [entryMode, setEntryMode] = useState<'empty' | 'draft' | 'canvas'>('empty');
  /** NEW — the "Describe a business rule" gate can be reached while the Settings dialog is
   * scrolled down (e.g. from the canvas toolbar's reopen button, or the empty-state's "Build
   * with BusinessRules" card lower on the page). Scroll it into view on entry so its heading
   * isn't left cut off behind the tab bar. */
  const draftGateRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (entryMode === 'draft') {
      draftGateRef.current?.scrollIntoView({ behavior: 'auto', block: 'start' });
    }
  }, [entryMode]);
  /** NEW — same reasoning as draftGateRef above: entering the canvas (e.g. via the empty
   * state's "Add first step" card, reached lower on a scrolled-down page) should scroll the
   * toolbar/canvas into view rather than leaving it cut off behind the tab bar. Only fires on
   * the actual transition into 'canvas', not on every re-render while already there. */
  const canvasTopRef = useRef<HTMLDivElement>(null);
  const prevEntryModeRef = useRef(entryMode);
  useEffect(() => {
    if (entryMode === 'canvas' && prevEntryModeRef.current !== 'canvas') {
      canvasTopRef.current?.scrollIntoView({ behavior: 'auto', block: 'start' });
    }
    prevEntryModeRef.current = entryMode;
  }, [entryMode]);
  /** NEW — Space name used only by the business-rule/draft-generation flow; kept separate from
   * `configName`, which stays owned by the Space panel's manual Load/Validate/Save flow. */
  const [draftConfigName, setDraftConfigName] = useState('');
  /** NEW — controlled value for WorkflowDraftPanel's textarea. Owned here (not inside that
   * component) so a suggestion chip can set it directly, and so the external Generate button
   * below can read the same current text. */
  const [draftDescription, setDraftDescription] = useState('');
  /** NEW — short pill label + icon shown to the user (n8n-reference style), each mapped to the
   * full sentence that actually fills draftDescription on click — the chip itself never carries
   * the long sentence as its visible text anymore. */
  const DRAFT_SUGGESTIONS: { label: string; sentence: string; icon: React.ElementType }[] = [
    {
      label: 'Approve leave requests',
      sentence: 'Approve leave requests over 3 days with a manager sign-off',
      icon: EventAvailableIcon,
    },
    {
      label: 'Flag mismatched invoices',
      sentence: 'Flag invoices with mismatched purchase order amounts',
      icon: ReceiptLongIcon,
    },
    {
      label: 'Notify on-call',
      sentence: 'Notify on-call when a critical ticket is unassigned for 30 minutes',
      icon: NotificationsActiveIcon,
    },
  ];
  /** NEW — true once a draft has been accepted onto the canvas; disables Load so it can't
   * overwrite the just-generated workflow. Resets to false when the user picks Skip/manual instead. */
  const [loadedViaDraft, setLoadedViaDraft] = useState(false);
  /** NEW — n8n-style on-demand node picker drawer, opened via the canvas "+" button (or the
   * empty state's "Add first step" card). The Inspector drawer opens instead whenever a node/edge
   * is selected and the picker isn't explicitly open — the two never show at the same time. */
  const [pickerOpen, setPickerOpen] = useState(false);
  /** PR #684 review (L4) — backs the "Unsaved changes" dialog guarding
   * handleBackToChannelWorkflow, replacing a window.confirm. */
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  /** NEW — Run test drawer (WorkflowRunTestPanel), opened automatically once Dry run/Execute
   * successfully starts a run-test. Takes priority over both the picker and inspector while
   * open, so selecting a node/edge or clicking "+" doesn't silently swap it out mid-run. */
  const [runTestOpen, setRunTestOpen] = useState(false);
  /** NEW — workflow_id/ref_id returned by the run-test that's currently open in the drawer above,
   * handed to WorkflowRunTestPanel so it starts polling immediately instead of waiting for its
   * own form/Start button to be used. */
  const [runTestInitialRun, setRunTestInitialRun] = useState<{ workflowId: string; refId: string } | null>(null);
  /** NEW — true while a Dry run/Execute click's run-test start request is in flight. */
  const [runTestBusy, setRunTestBusy] = useState(false);
  /** NEW — set if the last run-test start attempt failed; the run panel never opens in that
   * case, so this is shown right next to the buttons instead. */
  const [runTestError, setRunTestError] = useState<string | null>(null);
  /** NEW — where the next node picked from the palette should land: a plain append (the
   * original, unchanged behavior, used by the canvas "+" button and drag-and-drop), spliced
   * onto a specific node's dangling output ("afterNode"), or spliced into the middle of an
   * existing edge ("onEdge"). Reset back to 'append' immediately after each use so a later
   * plain "+" click never accidentally reuses stale insertion context. */
  const [pendingInsertion, setPendingInsertion] = useState<
    { kind: 'append' } | { kind: 'afterNode'; nodeId: string } | { kind: 'onEdge'; edgeId: string }
  >({ kind: 'append' });
  /** NEW — Auto-layout's current direction; also the value fed to LayoutDirectionContext so
   * PrimitiveNode's handle positions always match the layout just applied. Alternates each
   * click (see handleAutoLayout): first click applies 'horizontal', second reverts to
   * 'vertical' (the original, unchanged default/starting behavior), and so on. */
  const [layoutDirection, setLayoutDirection] = useState<LayoutDirection>('vertical');

  const [versionsLoading, setVersionsLoading] = useState(false);
  const [versionsError, setVersionsError] = useState<string | null>(null);
  const [versions, setVersions] = useState<WorkflowVersionSummary[] | null>(null);
  /** NEW — Versions dropdown: which version is currently loaded onto the canvas, and an
   * error message if a specific version's fetch fails (see handleSelectVersion —
   * GET /{name}/versions/{version} now loads any saved version, not just the latest). */
  const [selectedVersion, setSelectedVersion] = useState<number | null>(null);
  const [versionLoadWarning, setVersionLoadWarning] = useState<string | null>(null);

  // Action Catalog — loaded once, independently of everything above. A failure here only
  // disables the friendly palette tiles; raw primitives ("Advanced") keep working unaffected
  // (ref: 06_Frontend_API_Changes_Action_Catalog_2026-07-20.md §7).
  const [catalogActions, setCatalogActions] = useState<CatalogActionsByCategory | null>(null);
  const [catalogActionsById, setCatalogActionsById] = useState<Record<string, CatalogAction>>({});
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogError, setCatalogError] = useState<string | null>(null);
  // NEW — which external systems (Email, Team Approvals, Datasource, etc.) are currently active,
  // keyed by connection_id. Purely informational: surfaced as a tile-level notice in the palette
  // so a user picking an action whose connection isn't active understands why it may fail, before
  // adding it — never gates/disables a tile, and never carries credentials (ref: Workflow Canvas
  // — Save API Integration Guide §1).
  const [catalogConnectionsById, setCatalogConnectionsById] = useState<Record<string, CatalogConnection>>({});

  useEffect(() => {
    let cancelled = false;
    setCatalogLoading(true);
    getCatalogActions()
      .then((data) => {
        if (cancelled) return;
        setCatalogActions(data);
        const byId: Record<string, CatalogAction> = {};
        Object.values(data).forEach((list) => list.forEach((action) => { byId[action.action_id] = action; }));
        setCatalogActionsById(byId);
        setCatalogError(null);
      })
      .catch((e) => {
        if (cancelled) return;
        setCatalogError(e instanceof Error ? e.message : 'Failed to load action catalog');
      })
      .finally(() => {
        if (!cancelled) setCatalogLoading(false);
      });
    // Independent of the actions fetch above — a connections-list failure only means the tiles
    // lose their "connection active?" notice, the palette itself stays fully usable either way.
    getCatalogConnections()
      .then((connections) => {
        if (cancelled) return;
        const byId: Record<string, CatalogConnection> = {};
        connections.forEach((c) => { byId[c.connection_id] = c; });
        setCatalogConnectionsById(byId);
      })
      .catch(() => {
        /* non-fatal — palette tiles just render without a connection-status notice */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // NEW — Apps catalog, loaded once, purely to resolve a real per-app icon (app_key → app_image)
  // for CALL_API/AGENT nodes authored via the Action Catalog. Display-only: a failure here just
  // means nodes fall back to the generic primitive-type icon — nothing else on the canvas depends
  // on it, so it never blocks or breaks the rest of the tab.
  const [appIconByKey, setAppIconByKey] = useState<Record<string, string>>({});

  useEffect(() => {
    let cancelled = false;
    appsService
      .getApps(1, 100)
      .then((data) => {
        if (cancelled) return;
        const byKey: Record<string, string> = {};
        data.apps.forEach((app) => {
          if (app.app_key && app.app_image) byKey[app.app_key] = app.app_image;
        });
        setAppIconByKey(byKey);
      })
      .catch(() => {
        // Silent — nodes simply keep showing their generic primitive-type icon.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  /** NEW — opens the picker to add+connect a new node right after a dangling node's output. */
  const handleOpenPickerAfterNode = useCallback((nodeId: string) => {
    setSelectedNode(null);
    setSelectedEdge(null);
    setPendingInsertion({ kind: 'afterNode', nodeId });
    setPickerOpen(true);
  }, []);

  /** NEW — opens the picker to splice a new node into the middle of an existing edge. */
  const handleOpenPickerOnEdge = useCallback((edgeId: string) => {
    setSelectedNode(null);
    setSelectedEdge(null);
    setPendingInsertion({ kind: 'onEdge', edgeId });
    setPickerOpen(true);
  }, []);

  /** NEW — deletes one specific edge directly from its hover toolbar, without requiring the
   * click-to-select-then-Delete-selected flow the Inspector already offers. */
  const handleDeleteEdgeDirect = useCallback(
    (edgeId: string) => {
      setEdges((eds) => eds.filter((e) => e.id !== edgeId));
      setSelectedEdge((prev) => (prev && prev.id === edgeId ? null : prev));
    },
    [setEdges]
  );

  const displayNodes = useMemo(() => {
    // NEW — which nodes already have an outgoing edge, so PrimitiveNode knows whether to show
    // its dangling "+" button (only nodes with no outgoing connection get one).
    const sourceIdsWithOutgoingEdge = new Set(edges.map((e) => e.source));
    return nodes.map((n) => {
      // NEW — connectionId is set directly by buildActionNode (manual palette add), but nodes
      // that arrive via docToRfNodes (accepted AI draft, or Load) only carry actionId — resolve
      // connectionId from the Action Catalog lookup in that case so the icon still resolves.
      const connectionId = n.data?.connectionId ?? (n.data?.actionId ? catalogActionsById[n.data.actionId]?.connection_id : undefined);
      return {
        ...n,
        data: {
          ...n.data,
          running: n.id === runningNodeId,
          appIconUrl: connectionId ? appIconByKey[connectionId] : undefined,
          hasOutgoingEdge: sourceIdsWithOutgoingEdge.has(n.id),
          onAddAfter: () => handleOpenPickerAfterNode(n.id),
        },
      };
    });
  }, [nodes, edges, runningNodeId, appIconByKey, catalogActionsById, handleOpenPickerAfterNode]);

  // NEW — display-only edge variant: 'workflowEdge' type (hover Add/Delete toolbar) plus the two
  // new callbacks above. WorkflowEdge reads LayoutDirectionContext directly to decide straight
  // vs. bezier for a plain 1-to-1 link — same source of truth PrimitiveNode uses for handle
  // sides. `isBranch` (computed here, since it needs the full edges list) overrides that to
  // force a bezier curve at any fan-out (source has >1 outgoing edge) or fan-in (target has >1
  // incoming edge) point, even in horizontal layout — matching the reference flow, where only
  // plain node-to-node links are dead straight and branches/merges stay softly curved.
  // rfToDoc/buildValidatePayload continue to read the raw `edges` state directly, never this
  // derived array, so none of this reaches the saved graph payload.
  const displayEdges = useMemo<Edge<WorkflowEdgeData>[]>(() => {
    const outgoingCountBySource: Record<string, number> = {};
    const incomingCountByTarget: Record<string, number> = {};
    edges.forEach((e) => {
      outgoingCountBySource[e.source] = (outgoingCountBySource[e.source] ?? 0) + 1;
      incomingCountByTarget[e.target] = (incomingCountByTarget[e.target] ?? 0) + 1;
    });
    return edges.map((e) => ({
      ...e,
      type: WORKFLOW_EDGE_RF_TYPE,
      data: {
        ...e.data,
        isBranch: (outgoingCountBySource[e.source] ?? 0) > 1 || (incomingCountByTarget[e.target] ?? 0) > 1,
        onInsert: handleOpenPickerOnEdge,
        onDelete: handleDeleteEdgeDirect,
      },
    }));
  }, [edges, handleOpenPickerOnEdge, handleDeleteEdgeDirect]);

  const onConnect = useCallback(
    (connection: Connection) => {
      setEdges((eds) =>
        addEdge(
          {
            ...connection,
            id: `e_${connection.source}_${connection.target}_${eds.length}`,
            data: { condition: null },
            labelStyle: EDGE_LABEL_STYLE,
            labelBgStyle: EDGE_LABEL_BG_STYLE,
            labelBgPadding: EDGE_LABEL_BG_PADDING,
            labelBgBorderRadius: 4,
          },
          eds
        )
      );
    },
    [setEdges]
  );

  /** NEW — where a newly picked node should land, based on pendingInsertion: a plain grid slot
   * (unchanged original behavior), next to a dangling node's position, or at the midpoint of
   * the edge being split. Falls back to the grid slot if the referenced node/edge vanished. */
  const computeInsertPosition = (): { x: number; y: number } => {
    if (pendingInsertion.kind === 'afterNode') {
      const source = nodes.find((n) => n.id === pendingInsertion.nodeId);
      if (source) {
        return layoutDirection === 'horizontal'
          ? { x: source.position.x + 240, y: source.position.y }
          : { x: source.position.x, y: source.position.y + 160 };
      }
    }
    if (pendingInsertion.kind === 'onEdge') {
      const edge = edges.find((e) => e.id === pendingInsertion.edgeId);
      const source = edge && nodes.find((n) => n.id === edge.source);
      const target = edge && nodes.find((n) => n.id === edge.target);
      if (source && target) {
        return { x: (source.position.x + target.position.x) / 2, y: (source.position.y + target.position.y) / 2 };
      }
    }
    return gridPositionFor(nodes.length);
  };

  /** NEW — after the new node is added, wire up whatever connection pendingInsertion called for:
   * 'append' does nothing extra (unchanged original behavior — no auto-edge); 'afterNode' adds
   * one new edge from the dangling node; 'onEdge' removes the split edge and adds two new edges
   * around the inserted node, preserving the original edge's condition/label on the second half. */
  const applyPendingInsertionEdges = (newNodeId: string) => {
    if (pendingInsertion.kind === 'afterNode') {
      const { nodeId } = pendingInsertion;
      setEdges((eds) => [
        ...eds,
        {
          id: `e_${nodeId}_${newNodeId}_${eds.length}`,
          source: nodeId,
          target: newNodeId,
          data: { condition: null },
          labelStyle: EDGE_LABEL_STYLE,
          labelBgStyle: EDGE_LABEL_BG_STYLE,
          labelBgPadding: EDGE_LABEL_BG_PADDING,
          labelBgBorderRadius: 4,
        },
      ]);
    } else if (pendingInsertion.kind === 'onEdge') {
      const { edgeId } = pendingInsertion;
      setEdges((eds) => {
        const oldEdge = eds.find((e) => e.id === edgeId);
        if (!oldEdge) return eds;
        const rest = eds.filter((e) => e.id !== edgeId);
        const firstHalf: Edge = {
          id: `e_${oldEdge.source}_${newNodeId}_${rest.length}`,
          source: oldEdge.source,
          target: newNodeId,
          data: { condition: null },
          labelStyle: EDGE_LABEL_STYLE,
          labelBgStyle: EDGE_LABEL_BG_STYLE,
          labelBgPadding: EDGE_LABEL_BG_PADDING,
          labelBgBorderRadius: 4,
        };
        const secondHalf: Edge = {
          id: `e_${newNodeId}_${oldEdge.target}_${rest.length + 1}`,
          source: newNodeId,
          target: oldEdge.target,
          label: oldEdge.label,
          // PR #669 review (M5) — condition_op was dropped here (only `condition` was carried
          // over), so splicing a node onto an edge with a numeric comparison kept the misleading
          // "days_requested < 2" label while silently making the edge unconditional on save.
          data: {
            condition: (oldEdge.data?.condition as string | null | undefined) ?? null,
            condition_op: (oldEdge.data?.condition_op as BranchCondition | null | undefined) ?? null,
          },
          labelStyle: EDGE_LABEL_STYLE,
          labelBgStyle: EDGE_LABEL_BG_STYLE,
          labelBgPadding: EDGE_LABEL_BG_PADDING,
          labelBgBorderRadius: 4,
        };
        return [...rest, firstHalf, secondHalf];
      });
    }
  };

  // Click-to-add — unchanged grid placement for the default 'append' case (canvas "+" button),
  // now via the shared builder so drag-and-drop (below) can reuse the exact same node shape
  // without duplicating this logic. Also handles the two new insertion contexts (see above) and
  // closes the picker drawer after adding, matching n8n's "pick a node → drawer closes".
  const handleAddNode = (type: PrimitiveType) => {
    // NEW — a workflow name is required before anything can be added, no matter which UI path
    // triggered this (the picker's own render is also gated — see pickerOpen below — but this
    // guard is the actual mutation point, so it holds even if some other caller reaches here).
    if (!configName.trim()) return;
    const newNode = buildPrimitiveNode(type, computeInsertPosition());
    setNodes((nds) => [...nds, newNode]);
    applyPendingInsertionEdges(newNode.id);
    setPendingInsertion({ kind: 'append' });
    setPickerOpen(false);
  };

  /** NEW — add a node authored via a catalog action instead of a raw primitive (ref: 06_...md §3, §7). */
  const handleAddAction = (action: CatalogAction) => {
    if (!configName.trim()) return;
    const newNode = buildActionNode(action, computeInsertPosition());
    setNodes((nds) => [...nds, newNode]);
    applyPendingInsertionEdges(newNode.id);
    setPendingInsertion({ kind: 'append' });
    setPickerOpen(false);
  };

  /** NEW — drag-and-drop from the palette lands the node at the cursor instead of the grid slot.
   * Always a plain append (a drag targets a free canvas position, not a specific edge/node), so
   * this also resets any stale pendingInsertion left over from a picker that was opened but
   * closed without adding. */
  const handleAddNodeAt = (node: Node<PrimitiveNodeData>) => {
    if (!configName.trim()) return;
    setNodes((nds) => [...nds, node]);
    setPendingInsertion({ kind: 'append' });
    setPickerOpen(false);
  };

  /** NEW — button-triggered only (see CanvasSurface); re-flows node positions, never runs on Load.
   * Alternates direction each click: flips layoutDirection first, then lays out (and re-renders
   * node handles via LayoutDirectionContext) in that new direction — so positions and handle
   * sides always agree with each other. */
  const handleAutoLayout = useCallback(() => {
    const nextDirection: LayoutDirection = layoutDirection === 'vertical' ? 'horizontal' : 'vertical';
    setNodes((nds) => {
      const positions = computeAutoLayout(nds, edges, nextDirection);
      return nds.map((n) => (positions[n.id] ? { ...n, position: positions[n.id] } : n));
    });
    setLayoutDirection(nextDirection);
  }, [edges, setNodes, layoutDirection]);

  const handleUpdateNode = (id: string, patch: Partial<PrimitiveNodeData>) => {
    setNodes((nds) => nds.map((n) => (n.id === id ? { ...n, data: { ...n.data, ...patch } } : n)));
    setSelectedNode((prev) => (prev && prev.id === id ? { ...prev, data: { ...prev.data, ...patch } } : prev));
  };

  // PR #669 review (C1) — condition and condition_op are now two separate fields (matching the
  // orchestrator's own BranchCondition schema), set together so EdgeConditionEditor can enforce
  // "never both at once" (M2) by always clearing whichever one it isn't currently setting.
  const handleUpdateEdgeCondition = (id: string, update: { condition: string | null; conditionOp: BranchCondition | null }) => {
    setEdges((eds) =>
      eds.map((e) =>
        e.id === id
          ? {
              ...e,
              label: formatEdgeConditionLabel(update.condition, update.conditionOp),
              data: { condition: update.condition, condition_op: update.conditionOp },
              labelStyle: EDGE_LABEL_STYLE,
              labelBgStyle: EDGE_LABEL_BG_STYLE,
              labelBgPadding: EDGE_LABEL_BG_PADDING,
              labelBgBorderRadius: 4,
            }
          : e
      )
    );
    setSelectedEdge((prev) =>
      prev && prev.id === id ? { ...prev, data: { condition: update.condition, condition_op: update.conditionOp } } : prev
    );
  };

  const handleDeleteSelected = () => {
    if (selectedNode) {
      setNodes((nds) => nds.filter((n) => n.id !== selectedNode.id));
      setEdges((eds) => eds.filter((e) => e.source !== selectedNode.id && e.target !== selectedNode.id));
      setSelectedNode(null);
    }
    if (selectedEdge) {
      setEdges((eds) => eds.filter((e) => e.id !== selectedEdge.id));
      setSelectedEdge(null);
    }
  };

  const loadConfigByName = async (name: string) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    // PR #673 review (M2) — captured before the request so a reset that happens while this is
    // in flight can be detected below, rather than this response repopulating a cleared canvas.
    const myGeneration = resetGenerationRef.current;
    setLoadError(null);
    // Loading the latest version this way supersedes any specific version picked from the
    // Versions dropdown, so that stale selection (and any warning tied to it) shouldn't linger.
    setSelectedVersion(null);
    setVersionLoadWarning(null);
    setBusy(true);
    try {
      const doc = await getWorkflowConfig(trimmed);
      if (resetGenerationRef.current !== myGeneration) return;
      const loadedNodes = docToRfNodes(doc) as Node<PrimitiveNodeData>[];
      const loadedEdges = docToRfEdges(doc);
      setNodes(loadedNodes);
      setEdges(loadedEdges);
      setLayoutDirection(inferLayoutDirection(loadedNodes));
      setSelectedNode(null);
      setSelectedEdge(null);
      // PR #673 review (M1) — this load is the new "clean" baseline for the dirty check.
      lastPersistedDocRef.current = JSON.stringify(rfToDoc(loadedNodes, loadedEdges));
    } catch (e) {
      if (resetGenerationRef.current !== myGeneration) return;
      setLoadError(e instanceof Error ? e.message : 'Failed to load workflow config');
    } finally {
      if (resetGenerationRef.current === myGeneration) setBusy(false);
    }
  };

  const handleLoad = () => loadConfigByName(configName);

  /** NEW — lazy-loads the company-wide name list the first time the "Open…" dropdown is opened. */
  const handleOpenWorkflowsMenu = () => {
    if (workflowNames !== null || workflowNamesLoading) return;
    setWorkflowNamesError(null);
    setWorkflowNamesLoading(true);
    getWorkflowNames()
      .then((names) => setWorkflowNames(names))
      .catch((e) => setWorkflowNamesError(e instanceof Error ? e.message : 'Failed to load workflow list'))
      .finally(() => setWorkflowNamesLoading(false));
  };

  /** NEW — picking a workflow from the "Open…" dropdown sets the name field and loads it, same as manual Load. */
  const handleOpenWorkflow = (name: string) => {
    setConfigName(name);
    loadConfigByName(name);
  };

  /** NEW — "Back to Channel workflow": returns the tab to the exact blank "Untitled workflow"
   * + two-card chooser state, from any of the three places that button appears (manual
   * add-first-step flow, describe-a-business-rule flow, or an already-open/loaded workflow).
   * Nothing is saved here — this only clears what's on screen/in local state. An existing saved
   * workflow is untouched on the backend and can still be reopened via "Open…"/Load.
   *
   * PR #673 review (M1) — confirms before discarding anything not yet saved: edits made since
   * the last load/save, or nodes that were never saved/loaded at all (a half-built manual or
   * draft flow). The buttons that call this are disabled while busy/draftLoading/runTestBusy
   * (see their disabled props), so this never actually runs mid-request — the M2 generation
   * bump below is a defensive backstop for a response that was already in flight. */
  const handleBackToChannelWorkflow = () => {
    const hasUnsavedWork =
      nodes.length > 0 &&
      (lastPersistedDocRef.current === null ||
        JSON.stringify(rfToDoc(nodes, edges)) !== lastPersistedDocRef.current);
    // PR #684 review (L4) — this used window.confirm, the only place in this PR's set of
    // changes not using the MUI dialogs the rest of the workflow UI (and sibling
    // ChannelRuleFlowsView.tsx's own "Unsaved changes" guard) already use. Same guard logic,
    // now asked via a themed dialog instead of a native browser confirm.
    if (hasUnsavedWork) {
      setDiscardConfirmOpen(true);
      return;
    }
    performBackToChannelWorkflowReset();
  };

  const performBackToChannelWorkflowReset = () => {
    setDiscardConfirmOpen(false);
    resetGenerationRef.current += 1;
    lastPersistedDocRef.current = null;
    setConfigName('');
    setNodes([]);
    setEdges([]);
    setSelectedNode(null);
    setSelectedEdge(null);
    setPickerOpen(false);
    setRunTestOpen(false);
    setPendingInsertion({ kind: 'append' });
    setLayoutDirection('vertical');
    setSelectedVersion(null);
    setVersionLoadWarning(null);
    setLoadError(null);
    setValidationErrors(null);
    setSaveMessage(null);
    setSaveError(null);
    setRunTestError(null);
    setLoadedViaDraft(false);
    setDraftConfigName('');
    setDraftDescription('');
    setPendingDraft(null);
    setDraftError(null);
    // PR #673 review (minor) — versions and runTestInitialRun belong to the workflow being
    // left; without this they'd leak into whatever is opened/built next.
    setVersions(null);
    setVersionsError(null);
    setRunTestInitialRun(null);
    setEntryMode('empty');
  };

  const buildValidatePayload = (dryRunValue: boolean) => {
    const doc = rfToDoc(nodes, edges);
    return { ...doc, created_by: createdBy, backend, dry_run: dryRunValue };
  };

  // Validation never has live side effects either way, so it always checks with dry_run=true —
  // there's no user-facing dry-run/execute choice for Validate, only for Save (below).
  const handleValidate = async () => {
    if (!configName.trim()) return;
    setValidationErrors(null);
    setBusy(true);
    try {
      const result = await validateWorkflow(configName.trim(), buildValidatePayload(true));
      setValidationErrors(result.valid ? [] : result.errors);
    } catch (e) {
      setValidationErrors([e instanceof Error ? e.message : 'Validation request failed']);
    } finally {
      setBusy(false);
    }
  };

  // NEW — the canvas-bottom "Dry run" / "Execute" buttons no longer call Save (see
  // handleRunTest below) — dryRunValue here is only ever true, passed by the toolbar's own
  // Save button, kept as a parameter for backward compatibility with that one call site.
  const handleSave = async (dryRunValue: boolean) => {
    if (!configName.trim()) return;
    // PR #673 review (M2) — see loadConfigByName; a save that resolves after a reset must not
    // show a "Saved" message or set a baseline for the blank canvas it resolves onto.
    const myGeneration = resetGenerationRef.current;
    setSaveMessage(null);
    setSaveError(null);
    if (isReservedWorkflowName(configName)) {
      setSaveError(`"${configName.trim()}" is a reserved name and can't be used for a config`);
      return;
    }
    setBusy(true);
    try {
      const doc = await saveWorkflowConfig(configName.trim(), buildValidatePayload(dryRunValue));
      if (resetGenerationRef.current !== myGeneration) return;
      setSaveMessage(`Saved as version ${doc.version}${dryRunValue ? ' (dry run)' : ''}`);
      // NEW — the Load guard exists to protect an accepted-but-unsaved draft from being silently
      // overwritten; once it's actually persisted, the canvas IS the saved state, so there's
      // nothing left to protect and Load can safely resume working (see PR review M1 — this used
      // to have no reachable reset path once a draft had populated the canvas).
      setLoadedViaDraft(false);
      // NEW (PR review fix) — a successful save creates a new version, so the "not latest"
      // banner and Versions dropdown must reflect it immediately, not only after a manual
      // Refresh click. Also drop the cached company-wide name list so a newly-saved config
      // name shows up next time the "Open…" menu is opened, instead of only after reload.
      setSelectedVersion(doc.version ?? null);
      // PR #673 review (M1) — this save is the new "clean" baseline for the dirty check.
      lastPersistedDocRef.current = JSON.stringify(rfToDoc(nodes, edges));
      await handleRefreshVersions();
      setWorkflowNames(null);
    } catch (e) {
      if (resetGenerationRef.current !== myGeneration) return;
      setSaveError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      if (resetGenerationRef.current === myGeneration) setBusy(false);
    }
  };

  /**
   * NEW — what "Dry run"/"Execute" actually do now: start a real Temporal-backed run-test of
   * whatever's currently on the canvas, via the same run-test endpoint WorkflowRunTestPanel's
   * own form already used (POST /{name}/run-test) — never the save endpoint. Sends the graph
   * inline (nodes/edges) so a run can be tested before the workflow has ever been saved.
   * `explicitDryRun` is omitted (undefined) for "Dry run" so the backend's own default for an
   * inline-graph run-test (dry_run=true) applies; "Execute" passes `false` explicitly, since a
   * real run with live side effects should only ever happen on a deliberate ask.
   */
  const handleRunTest = async (explicitDryRun?: boolean) => {
    if (!configName.trim()) return;
    setRunTestError(null);
    setRunTestBusy(true);
    try {
      const inlineGraph = rfToDoc(nodes, edges);
      const payload: RunTestRequest = {
        company_id: '',
        sender_email: '',
        email_text: '',
        nodes: inlineGraph.nodes,
        edges: inlineGraph.edges,
        // PRISM7_TEMPORAL_FRONTEND_CHANGES.md FE-9 — a run-test previously sent no channel
        // context at all, so it couldn't behave like a real run for any channel-scoped node
        // (e.g. the datasource picker). channelId is only present when this tab is reached from
        // a channel's Settings drawer (LegacyWorkflowCanvasTab's own optional prop) — omitted
        // entirely for the company-wide "Channel Workflow" legacy builder path that has no
        // channel context, rather than sending a misleading empty string.
        ...(channelId ? { extra_context: { channel_id: channelId } } : {}),
        ...(explicitDryRun !== undefined ? { dry_run: explicitDryRun } : {}),
      };
      const result = await startRunTest(configName.trim(), payload);
      setRunTestInitialRun({ workflowId: result.workflow_id, refId: result.ref_id });
      setSelectedNode(null);
      setSelectedEdge(null);
      setPickerOpen(false);
      setRunTestOpen(true);
    } catch (e) {
      setRunTestError(e instanceof Error ? e.message : 'Failed to start run');
    } finally {
      setRunTestBusy(false);
    }
  };

  const handleGenerateDraft = async (description: string) => {
    if (!draftConfigName.trim()) {
      setDraftError('Enter a config name first');
      return;
    }
    // PR #673 review (M2) — see loadConfigByName; a generation that resolves after a reset
    // must not leave a stale pendingDraft that could surface the next time the panel opens.
    const myGeneration = resetGenerationRef.current;
    setDraftError(null);
    setDraftLoading(true);
    try {
      const result = await draftWorkflow(draftConfigName.trim(), description);
      if (resetGenerationRef.current !== myGeneration) return;
      setPendingDraft(result);
    } catch (e) {
      if (resetGenerationRef.current !== myGeneration) return;
      setDraftError(e instanceof Error ? e.message : 'Draft generation failed');
    } finally {
      if (resetGenerationRef.current === myGeneration) setDraftLoading(false);
    }
  };

  const handleAcceptDraft = () => {
    if (!pendingDraft) return;
    const acceptedNodes = docToRfNodes(pendingDraft) as Node<PrimitiveNodeData>[];
    setNodes(acceptedNodes);
    setEdges(docToRfEdges(pendingDraft));
    setLayoutDirection(inferLayoutDirection(acceptedNodes));
    setPendingDraft(null);
    setSelectedNode(null);
    setSelectedEdge(null);
    setEntryMode('canvas');
    // NEW — carry the draft's Space name into the sidebar's field so Save/Load/Validate
    // work immediately on the accepted workflow without the user retyping the name.
    setConfigName(draftConfigName);
    setLoadedViaDraft(true);
  };

  const handleDiscardDraft = () => setPendingDraft(null);

  const handleRefreshVersions = async () => {
    if (!configName.trim()) return;
    setVersionsError(null);
    setVersionsLoading(true);
    try {
      const result = await getWorkflowVersions(configName.trim());
      setVersions(result);
    } catch (e) {
      setVersionsError(e instanceof Error ? e.message : 'Failed to load versions');
    } finally {
      setVersionsLoading(false);
    }
  };

  /**
   * NEW — handles a pick from the Versions dropdown. As of the "Version History Load Fix"
   * (2026-07-24), GET /{name}/versions/{version} fetches that exact version's real content —
   * every saved version is now loadable, not just the latest — converted through the same
   * docToRfNodes/docToRfEdges path as every other load (ref: fix doc §2/§3).
   *
   * Loading a non-latest version stays fully editable (no read-only lock): Save always creates
   * the next version number regardless of which one is open — there is no update/overwrite
   * endpoint, so "restoring" v1 means loading it and saving it, which produces a new version
   * with that content (ref: fix doc §4/§5). The banner below the toolbar communicates this
   * plainly so nobody believes they've overwritten history in place.
   */
  const handleSelectVersion = async (version: number) => {
    const trimmedName = configName.trim();
    if (!trimmedName) return;
    // Same "don't silently overwrite an accepted-but-unsaved draft" guard as the Load button —
    // both paths ultimately replace canvas state (see LOADED_VIA_DRAFT_REASON).
    if (loadedViaDraft) {
      setSelectedVersion(version);
      setVersionLoadWarning(LOADED_VIA_DRAFT_REASON);
      return;
    }
    setVersionLoadWarning(null);
    setBusy(true);
    try {
      const doc = await getWorkflowConfigVersion(trimmedName, version);
      const loadedNodes = docToRfNodes(doc) as Node<PrimitiveNodeData>[];
      setNodes(loadedNodes);
      setEdges(docToRfEdges(doc));
      setLayoutDirection(inferLayoutDirection(loadedNodes));
      setSelectedNode(null);
      setSelectedEdge(null);
      setSelectedVersion(version);
    } catch (e) {
      setVersionLoadWarning(e instanceof Error ? e.message : `Couldn't load version ${version} — it may no longer exist.`);
    } finally {
      setBusy(false);
    }
  };

  // NEW — business-rules gate: the "Describe a business rule" box, styled as an AI-assistant-style
  // prompt (heading + rounded input + suggestion chips). Reached from the "Build with AI" card or
  // the canvas toolbar's reopen button. "Back" returns to the two-card chooser overlay below.
  if (entryMode === 'draft') {
    return (
      <Box
        ref={draftGateRef}
        sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%', minHeight: 600, pt: 8, px: 2 }}
      >
        <Box
          component="img"
          src={isDarkMode ? '/logo-white.png' : '/logo.png'}
          alt=""
          sx={{ height: 30, width: 'auto', maxWidth: 120, objectFit: 'contain', mb: 1 }}
        />
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, textAlign: 'center' }}>
          What would you like to automate?
        </Typography>

        <Box sx={{ width: '100%', maxWidth: 640 }}>
          <TextField
            label="Workspace name"
            value={draftConfigName}
            onChange={(e) => setDraftConfigName(e.target.value)}
            size="small"
            fullWidth
            error={isReservedWorkflowName(draftConfigName)}
            helperText={isReservedWorkflowName(draftConfigName) ? 'Reserved name — pick another' : undefined}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: 3,
                backgroundColor: 'background.paper',
                boxShadow: 2,
                '& fieldset': { borderColor: 'divider' },
                '&:hover fieldset': { borderColor: 'divider' },
                '&.Mui-focused fieldset': { borderColor: 'primary.main' },
                '&.Mui-focused': { boxShadow: '0 0 0 3px rgba(28,171,222,0.15)' },
              },
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <FieldHelp text={'The unique identifier for the workflow config this generated rule will be saved under.\nUse lowercase letters, numbers, and hyphens (e.g. leave-approval).\nReserved names "agent" and "auto-trigger" cannot be used — they are system-only paths.'} />
                </InputAdornment>
              ),
            }}
          />

          <Box
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 3,
              boxShadow: 2,
              backgroundColor: 'background.paper',
              p: 1,
              transition: 'border-color 0.15s, box-shadow 0.15s',
              '&:focus-within': {
                borderColor: 'primary.main',
                boxShadow: '0 0 0 3px rgba(28,171,222,0.15)',
              },
              '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
              '& .MuiOutlinedInput-root': { fontSize: '1rem' },
            }}
          >
            <WorkflowDraftPanel
              description={draftDescription}
              onDescriptionChange={setDraftDescription}
              hideTitle
              hideGenerateButton
              hideGeneratedContent
              loading={draftLoading}
              error={draftError}
              rawLlmNotes={pendingDraft?.raw_llm_notes ?? null}
              hasPendingDraft={Boolean(pendingDraft)}
              capability={pendingDraft?.capability ?? null}
              onGenerate={handleGenerateDraft}
              onAccept={handleAcceptDraft}
              onDiscard={handleDiscardDraft}
            />
          </Box>

          {/* Suggestion chips — sit between the description input and the Generate/Back row. */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2, justifyContent: 'flex-start' }}>
            {DRAFT_SUGGESTIONS.map((suggestion) => {
              const SuggestionIcon = suggestion.icon;
              return (
                <Chip
                  key={suggestion.label}
                  icon={<SuggestionIcon sx={{ fontSize: 16 }} />}
                  label={suggestion.label}
                  variant="outlined"
                  size="small"
                  onClick={() => setDraftDescription(suggestion.sentence)}
                  sx={{ cursor: 'pointer', textTransform: 'none' }}
                />
              );
            })}
          </Box>

          {/* NEW — Generate/Back now sit directly below the input field, right after it and
              before the generated-draft output below, per the reference layout. */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
            <Button
              variant="contained"
              size="small"
              disabled={draftLoading || !draftDescription.trim()}
              onClick={() => handleGenerateDraft(draftDescription.trim())}
              sx={{ textTransform: 'none' }}
            >
              {draftLoading ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
              Generate
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<ArrowBackIcon fontSize="small" />}
              onClick={handleBackToChannelWorkflow}
              disabled={busy || draftLoading || runTestBusy}
              sx={{ textTransform: 'none' }}
            >
              Back to Channel workflow
            </Button>
          </Box>

          {/* NEW — the generated draft's rationale + Accept/Discard stays a separate section,
              still outside the input field's bordered box, just now below Generate/Back instead
              of above it (hideGeneratedContent on the input instance above suppresses this same
              output from rendering a second time inside that box). */}
          <WorkflowDraftPanel
            description={draftDescription}
            onDescriptionChange={setDraftDescription}
            hideTitle
            hideGenerateButton
            hideInput
            loading={draftLoading}
            error={draftError}
            rawLlmNotes={pendingDraft?.raw_llm_notes ?? null}
            hasPendingDraft={Boolean(pendingDraft)}
            capability={pendingDraft?.capability ?? null}
            onGenerate={handleGenerateDraft}
            onAccept={handleAcceptDraft}
            onDiscard={handleDiscardDraft}
          />
        </Box>
      </Box>
    );
  }

  // NEW — n8n has no persistent config sidebar: the node picker and node inspector both appear
  // as on-demand right-side panels. `pickerOpen` wins when both would otherwise apply, so
  // opening the picker while a node is selected doesn't show both panels at once.
  const inspectorOpen = Boolean(selectedNode || selectedEdge) && !pickerOpen && !runTestOpen;
  // NEW — runTestOpen takes priority over both picker and inspector (see render below), so a
  // node/edge selection made while a run-test is in progress doesn't silently swap it out.
  const rightPanelOpen = runTestOpen || pickerOpen || inspectorOpen;
  // NEW — the two-card chooser now renders as an overlay ON TOP of the real (empty) canvas —
  // grid dots, zoom controls, and toolbar all visible behind it — instead of replacing the whole
  // tab with a separate blank page. Disappears the moment either card is used.
  const showEmptyOverlay = entryMode === 'empty' && nodes.length === 0;

  // NEW — saving is append-only (no update/overwrite endpoint), so editing a loaded non-latest
  // version and clicking Save always creates the NEXT version number, never overwrites the one
  // open on canvas. This banner makes that explicit so nobody believes they've restored or
  // overwritten history in place (ref: Version History "Load" Fix, 2026-07-24 §4/§5).
  const latestVersionNumber = versions && versions.length > 0 ? Math.max(...versions.map((v) => v.version)) : null;
  const oldVersionNotice =
    selectedVersion !== null && latestVersionNumber !== null && selectedVersion !== latestVersionNumber
      ? `You're viewing v${selectedVersion} (not the latest, v${latestVersionNumber}). Editing and saving will create v${latestVersionNumber + 1} — it will not change or overwrite v${selectedVersion}.`
      : null;

  return (
    <Box ref={canvasTopRef} sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 600 }}>
      <WorkflowToolbar
        configName={configName}
        onConfigNameChange={setConfigName}
        isReservedName={isReservedWorkflowName(configName)}
        workflowNames={workflowNames}
        workflowNamesLoading={workflowNamesLoading}
        workflowNamesError={workflowNamesError}
        onOpenWorkflowsMenu={handleOpenWorkflowsMenu}
        onOpenWorkflow={handleOpenWorkflow}
        onSave={() => handleSave(true)}
        saveDisabled={busy || !configName.trim() || isReservedWorkflowName(configName)}
        onLoad={handleLoad}
        loadDisabled={busy || !configName.trim() || loadedViaDraft}
        loadDisabledReason={LOADED_VIA_DRAFT_REASON}
        onValidate={handleValidate}
        validateDisabled={busy || !configName.trim()}
        onDeleteSelected={handleDeleteSelected}
        deleteSelectedDisabled={!selectedNode && !selectedEdge}
        versionsLoading={versionsLoading}
        versionsError={versionsError}
        versions={versions}
        onRefreshVersions={handleRefreshVersions}
        onSelectVersion={handleSelectVersion}
        selectedVersion={selectedVersion}
        versionLoadBusy={busy}
        versionLoadWarning={versionLoadWarning}
        oldVersionNotice={oldVersionNotice}
        loadError={loadError}
        validationErrors={validationErrors}
        saveMessage={saveMessage}
        saveError={saveError}
      />

      {/* NEW — shown whenever we're not already on the blank two-card chooser: the manual
          "Add first step" entry path, and an already-open/loaded workflow (e.g. opened via
          "Open…"/Load) alike. !showEmptyOverlay is the same condition the overlay itself uses
          (entryMode/nodes.length), so this and the overlay are always exact opposites — never
          shown together, never both hidden. Resets the canvas to the same blank "Untitled
          workflow" state shown by the two-card chooser (see handleBackToChannelWorkflow) — the
          saved config itself is untouched on the backend. */}
      {!showEmptyOverlay && (
        <Box sx={{ display: 'flex', mb: 1 }}>
          <Tooltip title="Leaves this workflow without saving and returns to the blank chooser">
            <span>
              <Button
                size="small"
                variant="outlined"
                startIcon={<ArrowBackIcon fontSize="small" />}
                onClick={handleBackToChannelWorkflow}
                disabled={busy || draftLoading || runTestBusy}
                sx={{ textTransform: 'none' }}
              >
                Back to Channel workflow
              </Button>
            </span>
          </Tooltip>
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: 2, flex: 1, minHeight: 0 }}>
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            position: 'relative',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
          }}
        >
          <LayoutDirectionContext.Provider value={layoutDirection}>
            <ReactFlowProvider>
              <CanvasSurface
                displayNodes={displayNodes}
                edges={displayEdges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onConnect={onConnect}
                onNodeClick={(_e, node) => {
                  setSelectedNode(node as Node<PrimitiveNodeData>);
                  setSelectedEdge(null);
                  setPickerOpen(false);
                }}
                onEdgeClick={(_e, edge) => {
                  setSelectedEdge(edge);
                  setSelectedNode(null);
                  setPickerOpen(false);
                }}
                onPaneClick={() => {
                  setSelectedNode(null);
                  setSelectedEdge(null);
                  setPickerOpen(false);
                }}
                onAddNodeAt={handleAddNodeAt}
                onAutoLayout={handleAutoLayout}
                onOpenDraftPanel={() => setEntryMode('draft')}
                onOpenPicker={() => {
                  if (!configName.trim()) return;
                  setSelectedNode(null);
                  setSelectedEdge(null);
                  setPendingInsertion({ kind: 'append' });
                  setPickerOpen(true);
                }}
                addStepDisabled={!configName.trim()}
                onDryRunSave={() => handleRunTest(undefined)}
                onExecute={() => handleRunTest(false)}
                saveActionsDisabled={busy || !configName.trim() || isReservedWorkflowName(configName)}
                runTestBusy={runTestBusy}
                runTestError={runTestError}
              />
            </ReactFlowProvider>
          </LayoutDirectionContext.Provider>

          {showEmptyOverlay && (
            <Box sx={{ position: 'absolute', inset: 0, zIndex: 5, pointerEvents: 'none', '& > *': { pointerEvents: 'auto' } }}>
              <EmptyWorkflowState
                onAddFirstStep={() => {
                  if (!configName.trim()) return;
                  setLoadedViaDraft(false);
                  setEntryMode('canvas');
                  setPendingInsertion({ kind: 'append' });
                  setPickerOpen(true);
                }}
                onBuildWithAI={() => setEntryMode('draft')}
                addFirstStepDisabled={!configName.trim()}
                addFirstStepDisabledReason="Enter a workflow name above first"
              />
            </Box>
          )}
        </Box>

        {rightPanelOpen && (
          <Box sx={{ width: 380, flexShrink: 0, overflowY: 'auto' }}>
            <Paper sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, boxShadow: 1, backgroundColor: 'background.paper' }}>
              {runTestOpen ? (
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                    <Tooltip title="Close" placement="top" arrow>
                      <IconButton size="small" onClick={() => setRunTestOpen(false)}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <WorkflowRunTestPanel
                    nodes={nodes}
                    onCurrentNodeChange={setRunningNodeId}
                    initialRun={runTestInitialRun}
                  />
                </Box>
              ) : pickerOpen && !configName.trim() ? (
                // NEW — the picker got opened (e.g. via a per-node/edge "+" that doesn't go
                // through the gated entry points) but there's still no workflow name: show why
                // instead of a seemingly-usable catalog whose own "Add" buttons would no-op
                // anyway (see the guard in handleAddNode/handleAddAction/handleAddNodeAt).
                <Box sx={{ p: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Enter a workflow name above before adding steps.
                  </Typography>
                </Box>
              ) : pickerOpen ? (
                <WorkflowPalette
                  onAdd={handleAddNode}
                  onAddAction={handleAddAction}
                  catalogActions={catalogActions}
                  catalogLoading={catalogLoading}
                  catalogError={catalogError}
                  catalogConnectionsById={catalogConnectionsById}
                />
              ) : (
                <WorkflowInspectorPanel
                  selectedNode={selectedNode}
                  selectedEdge={selectedEdge}
                  onUpdateNode={handleUpdateNode}
                  onUpdateEdgeCondition={handleUpdateEdgeCondition}
                  onDeleteSelected={handleDeleteSelected}
                  nodes={nodes}
                  edges={edges}
                  catalogActionsById={catalogActionsById}
                  channelId={channelId}
                />
              )}
            </Paper>
          </Box>
        )}
      </Box>

      {/* PR #684 review (L4) — replaces a window.confirm with the same themed Dialog pattern
          ChannelRuleFlowsView.tsx's own "Unsaved changes" guard already uses. */}
      <Dialog open={discardConfirmOpen} onClose={() => setDiscardConfirmOpen(false)}>
        <DialogTitle>Unsaved changes</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            You have unsaved changes that will be lost. Continue?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDiscardConfirmOpen(false)}>Cancel</Button>
          <Button onClick={performBackToChannelWorkflowReset} color="error">
            Discard &amp; continue
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export interface WorkflowCanvasTabProps {
  /** Business_rule_canvas branch — when present, renders the channel-scoped rule-flows view
   * (ChannelRuleFlowsView) instead of the original company-wide named-workflow builder above.
   * Omit (as before this branch) to keep the original behavior exactly. */
  channelId?: string;
  channelBusinessRules?: string;
  /** Fired with the freshly-recomputed business rules text right after a recipient edit's
   * confirm step succeeds — lets ChannelSettings push the update into the (separately mounted,
   * never-auto-refetching) Config tab immediately instead of requiring the whole drawer to be
   * closed and reopened. */
  onBusinessRulesChanged?: (updatedText: string) => void;
  /** business_rules_workflow_count — forwarded straight through to ChannelRuleFlowsView (channel-
   * scoped branch only; the legacy named-workflow builder has no dashboard-navigation concept). */
  onOpenDashboard?: () => void;
}

/** FIX 11b (frontend handover) — re-exposes ChannelRuleFlowsView's own guard handle unchanged;
 * LegacyWorkflowCanvasTab has no structural-edit concept of its own, so that path is always a
 * no-op (hasUnsavedChanges: false, guardNavigation: proceed immediately). */
export type WorkflowCanvasTabHandle = ChannelRuleFlowsViewHandle;

/**
 * Thin router: a stable top-level prop (channelId present or not) decides which of two
 * completely separate component trees renders — each owns its own independent hooks, so this
 * split never risks a React hooks-order violation the way an early return in the middle of
 * LegacyWorkflowCanvasTab's own hook sequence would. The ref forwarding below is unconditional
 * (called before either branch's return) so it never depends on which branch actually renders.
 */
const WorkflowCanvasTab = forwardRef<WorkflowCanvasTabHandle, WorkflowCanvasTabProps>(
  ({ channelId, channelBusinessRules, onBusinessRulesChanged, onOpenDashboard }, ref) => {
    const innerRef = useRef<ChannelRuleFlowsViewHandle>(null);
    useImperativeHandle(ref, () => ({
      hasUnsavedChanges: () => innerRef.current?.hasUnsavedChanges() ?? false,
      guardNavigation: (onProceed) => {
        if (innerRef.current) innerRef.current.guardNavigation(onProceed);
        else onProceed();
      },
    }), []);

    if (channelId) {
      return (
        <ChannelRuleFlowsView
          ref={innerRef}
          channelId={channelId}
          channelBusinessRules={channelBusinessRules}
          onBusinessRulesChanged={onBusinessRulesChanged}
          onOpenDashboard={onOpenDashboard}
        />
      );
    }
    return <LegacyWorkflowCanvasTab />;
  }
);

WorkflowCanvasTab.displayName = 'WorkflowCanvasTab';

// Named export — Channel Settings' separate "Channel Workflow" tab (channelSettingsTabs.ts's
// 'channelWorkflow' id) renders the original n8n-style named-workflow builder directly, since
// that tab must always show the legacy builder regardless of channelId (unlike the default
// export's router above, which is keyed off channelId presence for the 'workflow'/Business Rules
// Workflow tab).
export { LegacyWorkflowCanvasTab };
export default WorkflowCanvasTab;
